import axios from "axios";
import { API_URL } from "constants.js";
import type { InvoiceSendStatus } from "types/Invoice";
import {
  fileNameFromContentDisposition,
  saveBlob,
} from "./invoice-inbox.service";

type CreateInvoiceData = {
  seriesId?: string;
  date?: string;
  paymentDueDate?: string;
  /** The month to bill (YYYY-MM-01). Required for a recurring order; optional for a one-off one. */
  periodFrom?: string;
  /** Text of the optional service completion act (page 2 of the PDF); omit for none. */
  serviceActText?: string;
  /**
   * The order's CURRENT lines — every one of them, not only edited ones —
   * with the user's quantities/prices/title/description, keyed by the
   * externalCode `buildLinePayloadsFromOrder` (erp-backend
   * generate-from-order.js) stamps on the line it builds from the same order
   * item or product-group link — never by array position, which the
   * backend's `applyLineEditsToPayloads` has no way to use safely. Applied
   * inside the SAME create transaction: the submitted externalCodes must be
   * EXACTLY the set the backend builds from the CURRENT order (a line added
   * or removed since this preview was loaded also counts), or it answers 409
   * `ORDER_CHANGED` and creates nothing. A blank `productTitle` keeps the
   * line's current title; `description` is applied verbatim, so `""` clears
   * it rather than leaving it — omit `description` only when there is
   * genuinely nothing to say about the line either way.
   */
  lines?: Array<{
    externalCode: string;
    quantity: number;
    price: number;
    productTitle?: string;
    description: string;
  }>;
};

type PatchInvoiceData = {
  seriesId?: string;
  date?: string;
  paymentDueDate?: string;
  /** `null` removes the act. */
  serviceActText?: string | null;
  /** `null` clears a one-off invoice's month. */
  periodFrom?: string | null;
  periodTo?: string;
  items?: Array<Record<string, unknown>>;
  /** Drafts only: copy the customer's current name, address and codes onto the invoice. */
  refreshBuyer?: boolean;
};

export const createFromOrder = ({
  companyId,
  orderId,
  data = {},
}: {
  companyId: string;
  orderId: string;
  data?: CreateInvoiceData;
}) => {
  const url = `${API_URL}/${companyId}/orders/${orderId}/invoices`;
  return axios.post(url, data).then((response) => response.data);
};

export const fetchInvoice = ({
  companyId,
  invoiceId,
}: {
  companyId: string;
  invoiceId: string;
}) => {
  const url = `${API_URL}/${companyId}/invoices/${invoiceId}`;
  return axios.get(url).then((response) => response.data);
};

export const patchInvoice = ({
  companyId,
  invoiceId,
  data,
}: {
  companyId: string;
  invoiceId: string;
  data: PatchInvoiceData;
}) => {
  const url = `${API_URL}/${companyId}/invoices/${invoiceId}`;
  return axios.patch(url, data).then((response) => response.data);
};

export const confirmInvoice = ({
  companyId,
  invoiceId,
  send,
}: {
  companyId: string;
  invoiceId: string;
  /** Email the customer as part of confirming; without it the invoice is only numbered and rendered. */
  send: boolean;
}) => {
  const url = `${API_URL}/${companyId}/invoices/${invoiceId}/confirm`;
  return axios.post(url, { send }).then((response) => response.data);
};

type SendInvoiceResult = {
  emailSent: boolean;
  emailError: string | null;
  emailTo: string;
  send: InvoiceSendStatus;
};

/**
 * Emails a confirmed invoice. Answers 409 `ALREADY_SENT` (with `sentAt` and
 * `sentBy`) when it already went out and `force` is not set.
 */
export const sendInvoice = ({
  companyId,
  invoiceId,
  to,
  force = false,
}: {
  companyId: string;
  invoiceId: string;
  /** Recipients for this send only; without it the customer's invoice email is used. */
  to?: string;
  force?: boolean;
}): Promise<SendInvoiceResult> => {
  const url = `${API_URL}/${companyId}/invoices/${invoiceId}/send`;
  return axios.post(url, { to, force }).then((response) => response.data);
};

/** A refusal to a blob request is itself a Blob of JSON; parse it so `code` reaches the error mapper. */
async function withParsedRefusal(error: any) {
  const body = error?.response?.data;
  if (!(body instanceof Blob)) return error;
  try {
    error.response.data = JSON.parse(await body.text());
  } catch {
    // Not JSON — keep the error as it is.
  }
  return error;
}

/** Shows the saved draft as a watermarked PDF in a new tab; nothing is written on the server. */
export const previewInvoicePdf = async ({
  companyId,
  invoiceId,
}: {
  companyId: string;
  invoiceId: string;
}) => {
  // Opened before the (slow) render so the browser still treats it as the
  // click's own popup instead of blocking it.
  const tab = window.open("", "_blank");
  if (!tab) {
    // `code` lets invoiceErrorMessage show it in the user's language.
    throw Object.assign(new Error("Allow pop-ups to preview the PDF"), {
      code: "POPUP_BLOCKED",
    });
  }
  try {
    const response = await axios.get(
      `${API_URL}/${companyId}/invoices/${invoiceId}/preview`,
      { responseType: "blob" }
    );
    // Not revoked: the tab keeps reading the blob until it is closed.
    const pdfUrl = URL.createObjectURL(response.data);
    tab.location.href = pdfUrl;
  } catch (error) {
    tab.close();
    throw await withParsedRefusal(error);
  }
};

export const fetchSalesInvoices = ({
  companyId,
  status = "draft",
}: {
  companyId: string;
  status?: "draft" | "confirmed";
}) => {
  const url = `${API_URL}/${companyId}/sales-invoices`;
  return axios
    .get(url, { params: { status } })
    .then((response) => response.data);
};

/** One currency's figures; amounts are in that currency. */
export type SalesRegisterTotals = {
  currency: string;
  count: number;
  net: number;
  vat: number;
  gross: number;
};

/** A month of the register that has invoices: `period` is `YYYY-MM`. */
export type SalesRegisterPeriod = {
  period: string;
  count: number;
  totals: SalesRegisterTotals[];
};

export type SalesRegisterRow = {
  id: string;
  /** The number printed on the invoice, e.g. "DV-260930-1". */
  number: string;
  date: string;
  paymentDueDate: string | null;
  customer: string;
  customerName: string;
  customerCode: string | null;
  customerVatCode: string | null;
  currency: string;
  net: number;
  vat: number;
  gross: number;
  /** VAT classifier codes of the invoice lines, e.g. ["PVM1"]. */
  pvmCodes: string[];
  file: string | null;
};

export type SalesRegisterMonth = {
  period: string;
  rows: SalesRegisterRow[];
  totals: SalesRegisterTotals[];
};

/** Months with issued invoices, newest first. */
export const fetchSalesRegisterPeriods = (
  companyId: string
): Promise<SalesRegisterPeriod[]> =>
  axios
    .get(`${API_URL}/${companyId}/sales-invoices/register`)
    .then((response) => response.data.periods);

export const fetchSalesRegisterMonth = (
  companyId: string,
  period: string
): Promise<SalesRegisterMonth> =>
  axios
    .get(`${API_URL}/${companyId}/sales-invoices/register/${period}`)
    .then((response) => response.data);

/** Saves the month's register as a PDF in the given language ("lt" or "en"). */
export const downloadSalesRegisterPdf = async (
  companyId: string,
  period: string,
  lang: "lt" | "en"
): Promise<void> => {
  const response = await axios.get(
    `${API_URL}/${companyId}/sales-invoices/register/${period}/pdf`,
    { params: { lang }, responseType: "blob" }
  );
  saveBlob(
    response.data,
    fileNameFromContentDisposition(
      response.headers["content-disposition"],
      `sales-invoice-register-${period}.pdf`
    )
  );
};

export const fetchCustomerInvoices = ({
  companyId,
  customerId,
  status = "all",
}: {
  companyId: string;
  customerId: string;
  status?: "all" | "draft" | "confirmed";
}) => {
  const url = `${API_URL}/${companyId}/customers/${customerId}/invoices`;
  return axios
    .get(url, { params: { status } })
    .then((response) => response.data);
};

/** One product line of an order, as it will appear on the invoice. */
export type InvoiceableOrderLine = {
  /**
   * The identity the invoice line will carry. Edits travel back keyed by it,
   * so they attach to the right line regardless of ordering.
   */
  externalCode: string;
  title: string;
  description?: string;
  quantity: number;
  price: number;
  total: number;
};

/** An order the "Create invoice" modal may offer: signed, one-off, uninvoiced. */
export type InvoiceableOrder = {
  id: string;
  number?: number | null;
  _number?: string | null;
  date?: string | null;
  validThrough?: string | null;
  description?: string | null;
  /** What will be invoiced — shown in the row's collapsible product list. */
  lines: InvoiceableOrderLine[];
  lineCount: number;
  totalPrice?: number | null;
  totalVat?: number | null;
  finalPrice?: number | null;
};

export type BulkInvoiceResult = {
  orderId: string;
  ok: boolean;
  invoiceId?: string;
  number?: string | null;
  status?: string;
  alreadyExists?: boolean;
  emailSent?: boolean;
  emailSkipReason?: string | null;
  code?: string;
  message?: string;
};

export const fetchInvoiceableOrders = ({
  companyId,
  customerId,
}: {
  companyId: string;
  customerId: string;
}): Promise<{ orders: InvoiceableOrder[] }> => {
  const url = `${API_URL}/${companyId}/customers/${customerId}/invoiceable-orders`;
  return axios.get(url).then((response) => response.data);
};

/** An order to invoice, with the lines to keep and the values to use. */
export type BulkInvoiceOrder = {
  id: string;
  /** Omit to invoice the order exactly as it stands. */
  lines?: Array<{ externalCode: string; quantity: number; price: number }>;
};

/**
 * One invoice per selected order (never one spanning several).
 * `mode: "build"` also confirms each (number and PDF), and emails the customer
 * when `send` is set.
 */
export const createInvoicesBulk = ({
  companyId,
  customerId,
  orders,
  mode,
  send,
}: {
  companyId: string;
  customerId: string;
  orders: BulkInvoiceOrder[];
  mode: "draft" | "build";
  send: boolean;
}): Promise<{
  mode: string;
  results: BulkInvoiceResult[];
  createdCount: number;
  failedCount: number;
}> => {
  const url = `${API_URL}/${companyId}/customers/${customerId}/invoices/bulk`;
  return axios.post(url, { orders, mode, send }).then((response) => response.data);
};

/**
 * Deleting a draft releases its order back into the create-invoice list. For
 * a RECURRING order's draft, it also records the billed period as skipped
 * (invoices/delete-draft.js) so the cron's catch-up does not silently
 * re-create the very draft just removed — `skipped`/`periodFrom` say so.
 */
export const deleteDraftInvoice = ({
  companyId,
  invoiceId,
  keepPeriodOwed = false,
}: {
  companyId: string;
  invoiceId: string;
  /**
   * Undoing a draft this same action just created: delete it WITHOUT marking
   * its month as skipped (deleting a recurring draft normally does).
   */
  keepPeriodOwed?: boolean;
}): Promise<{ deleted: boolean; orderId: string | null; skipped?: boolean; periodFrom?: string }> => {
  const url = `${API_URL}/${companyId}/invoices/${invoiceId}`;
  return axios
    .delete(url, keepPeriodOwed ? { params: { skipPeriod: false } } : undefined)
    .then((response) => response.data);
};
